import { AuthService } from '~/app-modules/auth/services/auth-service';
import {
  AccessControlService,
  CheckSatisfyPermissionsResult,
} from '~/app-modules/auth/services/access-control-service';
import { PermissionType } from '~/app-modules/user-access-right/clients/enums/permission-type';
import { LocalizationsStore } from '~/app-modules/core/stores/localizations.store';
import { UiDictionaryType } from '~/app-modules/core/enums/UiDictionaryType';

const pathsWithoutRequiredPermissions = ['/', '/auth/callback', '/user'];

export default defineNuxtRouteMiddleware(async (to, _from) => {
  const {
    meta: { requiresAuth = true, permissions = [] },
    name,
    path,
  } = to;

  const authService = useIOC(AuthService);
  const userLoggedIn = authService.isLoggedIn;

  // перехват редиректа от sso (на уровне мидлвары работает без лоадеров и очень быстро)
  if (path === '/auth/callback') {
    try {
      const { code } = useRoute().query as { code: string };
      await authService.signIn(code);

      return '/';
    } catch (error) {
      console.error('Ошибка авторизации', error);
      return '/auth';
    }
  }

  if (to.matched.length === 0) {
    return showError(
      createError({
        message: 'Страница не найдена',
      }),
    );
  }

  const permissionsArray = permissions as PermissionType[];
  if (
    requiresAuth &&
    permissionsArray.length === 0 &&
    !pathsWithoutRequiredPermissions.includes(path)
  ) {
    return showError(
      createError({
        message: `Неверная конфигурация разрешений для страницы: ${path}`,
      }),
    );
  }

  if (name === 'auth' && !userLoggedIn) return;

  if (!requiresAuth) return;

  const accessControlService = useIOC(AccessControlService);
  let checkSatisfyPermissionsResult: CheckSatisfyPermissionsResult;
  try {
    checkSatisfyPermissionsResult =
      await accessControlService.checkSatisfyPermissions(permissionsArray);
  } catch (error) {
    console.error('Ошибка проверки доступа', error);
    authService.logout();
    return '/auth';
  }

  if (!checkSatisfyPermissionsResult.hasAuth) {
    return '/auth';
  }

  if (checkSatisfyPermissionsResult.notSatisfiedPermissions.length > 0) {
    const localizationsStore = useIOC(LocalizationsStore);
    const permissionsNamesForUI = await localizationsStore.getDictionaryByType(
      UiDictionaryType.PermissionType,
    );
    const notSatisfiedPermissionsNamesForUI =
      checkSatisfyPermissionsResult.notSatisfiedPermissions.map((p) =>
        permissionsNamesForUI.get(p),
      );
    return showError(
      createError({
        message: `Доступ запрещён. Требуемые разрешения: ${notSatisfiedPermissionsNamesForUI.join(
          ', ',
        )}`,
      }),
    );
  }
});

declare module '#app' {
  interface PageMeta {
    /**
     * Установлена по умолчанию как true
     */
    requiresAuth?: boolean;
  }
}

declare module 'vue-router' {
  interface RouteMeta {
    /**
     * Установлена по умолчанию как true
     */
    requiresAuth?: boolean;
  }
}
